import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import track from 'react-tracking';
import PropTypes from 'prop-types';
import memoizeOne from 'memoize-one';
import { injectIntl } from 'gatsby-plugin-react-intl';

import { getCourseRunEnrollButtonText } from '@utils/courseRunSelection';
import { withLocation } from '@utils/navigation';
import withUser from '@utils/withUser';
import { enterpriseUser } from '@data/actions/enterprise';
import courseRunShape from '@prospectus/common/types/courseRunShape';

import { intlShape } from '@src/packages/common/types/intlShape';
import EnrollButtonLink from './EnrollButtonLink';

function BaseEnrollButtonCommon({
  defaultCourseRun,
  courseRun: propCourseRun,
  userEnrollments,
  intl,
  product,
  uuid,
  buttonClass,
  buttonVariant,
  hideAsSecondaryButton,
  trackingID,
  emailOptIn,
  className,
  enrolledInOneRun,
  isEnterpriseUser,
  requestIsEnterpriseUser,
  owners,
  cohesionVariant,
  cohesionCategory,
  cohesionName,
}) {
  const [userEnrolled, setUserEnrolled] = useState(false);

  const memoizedSetUserIsEnrolled = memoizeOne((enrollments, courseRun) => {
    const key = courseRun?.key || defaultCourseRun?.key;
    const enrolled = enrollments.some(({ courseId }) => courseId === key);
    setUserEnrolled(enrolled);
  });

  useEffect(() => {
    memoizedSetUserIsEnrolled(userEnrollments, propCourseRun || defaultCourseRun);
    requestIsEnterpriseUser();
  }, [userEnrollments, propCourseRun, defaultCourseRun, requestIsEnterpriseUser]);

  const courseRun = propCourseRun || defaultCourseRun;

  const getLabel = () => getCourseRunEnrollButtonText(courseRun, intl, userEnrolled);

  return (
    <EnrollButtonLink
      courseRun={courseRun}
      product={product}
      uuid={uuid}
      buttonClass={buttonClass}
      buttonVariant={buttonVariant}
      buttonLabel={getLabel()}
      hideAsSecondaryButton={hideAsSecondaryButton}
      trackingID={trackingID}
      emailOptIn={emailOptIn}
      className={className}
      makeButtonLight={enrolledInOneRun}
      enterpriseUser={isEnterpriseUser}
      owners={owners}
      cohesionVariant={cohesionVariant}
      cohesionCategory={cohesionCategory}
      cohesionName={cohesionName}
    >
      {getLabel()}
    </EnrollButtonLink>
  );
}

BaseEnrollButtonCommon.propTypes = {
  defaultCourseRun: PropTypes.shape(courseRunShape).isRequired,
  courseRun: PropTypes.shape(courseRunShape),
  userEnrollments: PropTypes.arrayOf(
    PropTypes.shape({
      courseId: PropTypes.string.isRequired,
      mode: PropTypes.string.isRequired,
    }),
  ),
  intl: intlShape.isRequired,
  product: PropTypes.string,
  uuid: PropTypes.string.isRequired,
  buttonClass: PropTypes.string,
  buttonVariant: PropTypes.string,
  hideAsSecondaryButton: PropTypes.bool,
  trackingID: PropTypes.string,
  emailOptIn: PropTypes.bool,
  className: PropTypes.string,
  enrolledInOneRun: PropTypes.bool,
  isEnterpriseUser: PropTypes.bool,
  requestIsEnterpriseUser: PropTypes.func,
  owners: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string,
    }),
  ).isRequired,
  cohesionVariant: PropTypes.string,
  cohesionCategory: PropTypes.string,
  cohesionName: PropTypes.string,
};

BaseEnrollButtonCommon.defaultProps = {
  userEnrollments: [],
  emailOptIn: false,
  product: 'course',
  buttonClass: '',
  buttonVariant: 'brand',
  hideAsSecondaryButton: false,
  trackingID: undefined,
  courseRun: null,
  className: null,
  enrolledInOneRun: false,
  isEnterpriseUser: false,
  requestIsEnterpriseUser: () => {},
  cohesionVariant: null,
  cohesionCategory: null,
  cohesionName: null,
};

const mapStateToProps = store => ({
  defaultCourseRun: store.activeCourseRun,
  isEnterpriseUser: store.enterpriseUser,
});

const mapDispatchToProps = dispatch => ({
  requestIsEnterpriseUser() {
    dispatch(enterpriseUser());
  },
});

const EnrollButtonCommon = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withUser(withLocation(injectIntl(BaseEnrollButtonCommon))));

export default track({
  component: 'enroll-button',
})(EnrollButtonCommon);
